import { forwardRef, type SelectHTMLAttributes } from 'react';
import { cn } from '@/utils/cn';
import { Field, controlBase, controlSize, type ControlSize } from './Field';

// `size` omitted for the same reason as Input: natively it is a visible-row count.
interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  label?: string;
  error?: string;
  hint?: string;
  options?: { value: string | number; label: string }[];
  placeholder?: string;
  size?: ControlSize;
  /** Layout for the field as a whole — grid span, width. `className` styles the select itself. */
  fieldClassName?: string;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    { label, error, hint, options, placeholder, size = 'sm', className, fieldClassName, id, children, ...props },
    ref,
  ) => {
    const inputId = id ?? label?.toLowerCase().replace(/\s+/g, '-');

    return (
      <Field label={label} htmlFor={inputId} error={error} hint={hint} className={fieldClassName}>
        <select
          ref={ref}
          id={inputId}
          className={cn(controlBase(!!error), controlSize[size], 'px-3', className)}
          {...props}
        >
          {placeholder && <option value="">{placeholder}</option>}
          {options?.map(o => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
          {children}
        </select>
      </Field>
    );
  },
);

Select.displayName = 'Select';
